import { NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Optional,
	Output
} from '@angular/core';
import {
	PreviewSlide,
	ReportDateRange,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import { applyContentItemOverrides } from '../../../../services/report-preview-utils';
import { ReportLanguageService } from '../../../../services/report-language.service';
import { reportMessage } from '../../../../services/report-messages';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';
import { ReportFigureFlagsComponent } from '../../components/report-figure-flags/report-figure-flags.component';

@Component({
	standalone: true,
	selector: 'app-cover-page-slide',
	templateUrl: './cover-page-slide.component.html',
	styleUrls: ['./cover-page-slide.component.scss'],
	imports: [
		NgIf,
		ReportFigureFlagsComponent,
		ReportInlineTextComponent,
		ReportSlideBrandComponent
	]
})
export class CoverPageSlideComponent implements OnChanges {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();

	/** The title and description the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	description = '';
	dateLabel = '';
	// Blue mono kicker above the hero, e.g. "SOCIAL LISTENING REPORT · 2026".
	eyebrowText = '';
	periodLabel = 'Period';
	// Hero split into a dark head + a blue-accented last word (the LOLLY-Figma
	// two-tone hero). Single-word titles render entirely in the head (all dark).
	titleHead = '';
	titleAccent = '';
	// Hero size (px) picked from the title length so a short title gets the
	// dramatic LOLLY-Figma scale while a long one (titles vary per report) still
	// fits the 1440×823 export without overflowing.
	heroFontSizePx = 115;

	constructor(
		@Optional() private reportLanguage: ReportLanguageService | null
	) {}

	ngOnChanges(): void {
		if (!this.item || !this.dateRange) return;
		const rawSlide = this.buildSlide();
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.slideBuilt.emit(slide);
	}

	private buildSlide(): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		return {
			id: `${prefix}_slide`,
			slideType: 'cover_page',
			title: this.item.title || 'Report',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Report',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_subtitle`,
					kind: 'subtitle',
					visible: true,
					content: {
						text: this.item.description || '',
						color: '#535473'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				}
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);
		const titleWidget = slide.widgets.find(
			w => w.kind === 'title' && !hidden.has(w.id)
		) as TitleWidget | undefined;
		const subtitleWidget = slide.widgets.find(
			w => w.kind === 'subtitle' && !hidden.has(w.id)
		) as TitleWidget | undefined;
		const dateWidget = slide.widgets.find(
			w => w.kind === 'date_range' && !hidden.has(w.id)
		) as TitleWidget | undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.description = subtitleWidget?.content.text ?? '';
		this.dateLabel =
			dateWidget?.content.text ?? slide.dateRange?.label ?? '';

		this.eyebrowText = this.buildEyebrow(slide.dateRange);
		this.heroFontSizePx = this.computeHeroFontSize(this.title);
		this.splitHeroTitle(this.title);
	}

	// Blue the trailing word (the LOLLY-Figma two-tone hero). A single-word
	// title stays fully dark to avoid an all-accent headline.
	private splitHeroTitle(title: string): void {
		const words = title.trim().split(/\s+/).filter(Boolean);
		if (words.length > 1) {
			this.titleAccent = words[words.length - 1];
			// No trailing space: the head and the accent <span> sit on separate
			// template lines, so Angular's compile-time whitespace collapse
			// already inserts exactly one space between them.
			this.titleHead = words.slice(0, -1).join(' ');
		} else {
			this.titleHead = title;
			this.titleAccent = '';
		}
	}

	// "SOCIAL LISTENING REPORT" + the report's end year when a range is known.
	private buildEyebrow(dateRange?: ReportDateRange): string {
		const base = reportMessage(
			this.reportLanguage?.language,
			'cover.eyebrow'
		);
		const iso = dateRange?.end || dateRange?.start;
		if (!iso) return base;
		const year = new Date(iso).getUTCFullYear();
		return Number.isFinite(year) ? `${base} · ${year}` : base;
	}

	// Step the hero down as the title grows, keeping it to ~2–3 lines within the
	// 1440px-wide export card. Tracking/line-height are em-relative in the SCSS,
	// so only the size needs to change here.
	private computeHeroFontSize(title: string): number {
		const length = title.trim().length;
		if (length <= 36) return 115;
		if (length <= 52) return 92;
		if (length <= 72) return 68;
		if (length <= 100) return 52;
		return 44;
	}
}
